import * as React from 'react';

import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@wwe-portal/ui/lib/lib';

import { LegacyCloseIcon } from '../icons/legacy-close-icon';

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    className={cn(
      'z-toast fixed inset-x-0 top-5 mx-auto flex max-h-screen w-full flex-col-reverse items-center p-5 text-sm',
      className
    )}
    ref={ref}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  'font-poppins max-w-[350px] border-[#ffffff1a] bg-[#e2e4f51a] pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-[55px] border p-2 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
  {
    variants: {
      variant: {
        success: '',
        error: '',
      },
    },
    defaultVariants: {
      variant: 'success',
    },
  }
);

const toastContentVariants = cva(
  'w-full flex gap-5 items-center justify-around rounded-[50px] py-[9px] pl-[8px] pr-[15px] text-inlineTextColor3',
  {
    variants: {
      variant: {
        success: 'border bg-[#edefff] text-green800',
        error: 'bg-[#ffeded] text-[#dc2742]',
      },
    },
  }
);

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({ className, variant, children, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      className={cn(toastVariants({ variant }), className)}
      ref={ref}
      {...props}
    >
      <div className={cn(toastContentVariants({ variant }))}>
        <div className="flex items-center justify-center rounded-full bg-white p-[5px]">
          <ToastIcon variant={variant} />
        </div>
        {children}
      </div>
    </ToastPrimitives.Root>
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    className={cn(
      'ring-offset-background hover:bg-secondary focus:ring-ring group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
      className
    )}
    ref={ref}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    className={cn('m-0 border-0 bg-transparent p-1', className)}
    ref={ref}
    toast-close=""
    {...props}
  >
    <LegacyCloseIcon className="h-[10px] w-[10px]" height={10} width={10} />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

function ToastSuccessIcon() {
  return (
    <svg
      fill="#0dd39c"
      height="16"
      viewBox="0 0 512 512"
      width="16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M256 512a256 256 0 100-512 256 256 0 100 512zm113-303L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z" />
    </svg>
  );
}

function ToastErrorIcon() {
  return (
    <svg
      fill="#dc2742"
      height="16"
      viewBox="0 0 512 512"
      width="16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M256 512a256 256 0 100-512 256 256 0 100 512zm0-384c13.3 0 24 10.7 24 24v112c0 13.3-10.7 24-24 24s-24-10.7-24-24V152c0-13.3 10.7-24 24-24zm-32 224a32 32 0 1164 0 32 32 0 11-64 0z" />
    </svg>
  );
}

const ToastIcon: React.FC<VariantProps<typeof toastVariants>> = ({
  variant,
}) => {
  if (variant === 'error') return <ToastErrorIcon />;
  if (variant === 'success') return <ToastSuccessIcon />;
  return null;
};

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastClose,
  ToastAction,
  ToastIcon,
};
